import { PROVIDER_ERROR_CODE } from '@steerfi/shared';
import { IconShieldExclamation } from '@tabler/icons-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { ProviderApiRow } from '@/api/providers';
import { deleteProvider } from '@/api/providers';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface DeleteProviderDialogProps {
  open: boolean;
  provider: ProviderApiRow | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

/** confirm = ask before DELETE; blocker = same dialog after API returns “has active relationships”. */
type DialogState = 'confirm' | 'blocker';

export const DeleteProviderDialog: React.FC<DeleteProviderDialogProps> = ({
  open,
  provider,
  onOpenChange,
  onSuccess,
}) => {
  const [state, setState] = useState<DialogState>('confirm');
  const [activeRelCount, setActiveRelCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setState('confirm');
      setErrorMsg(null);
      setActiveRelCount(0);
    }
  }, [open]);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setState('confirm');
      setErrorMsg(null);
    }
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    if (!provider) return;
    try {
      setSubmitting(true);
      setErrorMsg(null);
      const result = await deleteProvider(provider.party_id);
      if (result.ok) {
        handleOpenChange(false);
        onSuccess();
      } else if (result.code === PROVIDER_ERROR_CODE.HAS_ACTIVE_RELATIONSHIPS) {
        setActiveRelCount(result.activeRelationshipCount);
        setState('blocker');
      } else {
        setErrorMsg(result.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        {state === 'confirm' && (
          <>
            <DialogHeader>
              <DialogTitle>Delete provider</DialogTitle>
              <DialogDescription className="text-foreground/90">
                Are you sure you want to delete <strong>{provider.provider_name}</strong> (
                {provider.provider_code})? This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {errorMsg && <p className="text-sm text-destructive">{errorMsg}</p>}
            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
                onClick={handleConfirm}
                disabled={submitting}
              >
                {submitting ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </>
        )}
        {state === 'blocker' && (
          <>
            <DialogHeader className="space-y-0 pr-10">
              <DialogTitle className="flex items-start gap-3 text-left text-base font-semibold leading-snug">
                <IconShieldExclamation
                  size={22}
                  className="mt-0.5 shrink-0 text-amber-500"
                  aria-hidden
                />
                <span>Cannot delete provider</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm text-foreground/90">
              <p>
                Cannot delete <strong>{provider.provider_name}</strong> ({provider.provider_code}) -{' '}
                {activeRelCount} active relationship{activeRelCount !== 1 ? 's' : ''} exist.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Detach or deactivate the provider's active relationships first, then try again.
              </p>
            </div>
            <DialogFooter className="mt-2 sm:justify-end">
              <Button
                type="button"
                className="rounded-md bg-black px-5 text-white hover:bg-black/90 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-zinc-200"
                onClick={() => handleOpenChange(false)}
              >
                Got it
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
